import { getString, isString } from "../utility/helper";

export const RoutingKeys = {
  MAIN_MENU: "mainmenu",
  DEALER_SEARCH: "find_a_dealer",
  MORE: "more",
  SHOP: "shop",
  SERIES: "series",
  ACCOUNT: "account",
  MY_PORSCHE: "my_porsche",
  VEHICLE_PURCHASE: "vehicle_purchase",
  EXPERIENCE: "experience",
  SERVICES: "services",
  MODELS: "models",
  ELECTRIC_OWNERSHIP: "electric_ownership",
} as const;

export enum RoutingKeyPointers {
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  LEVEL_ZERO = -1,
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  LEVEL_ONE = 0,
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  LEVEL_TWO = 1,
}

export type RoutingKey = (typeof RoutingKeys)[keyof typeof RoutingKeys];

export interface RoutingKeyWithClick {
  initialRoutingKey: string;
  isKeyboardClick: boolean;
}

export function constructRoutingKey(...args: string[]): string {
  return args
    .reduce<string[]>(
      (segments, arg) => [...segments, ...getString(arg).split("/")],
      [],
    )
    .filter(Boolean)
    .join("/");
}

export function createRoutePath(routeAsString: string): string[] {
  const parts = routeAsString.split("/");

  return parts.map((_, index) => parts.slice(0, index + 1).join("/"));
}

export function findTopLevelRoutingKey(
  activeRoutingKey: string,
): RoutingKey | undefined {
  if (isString(activeRoutingKey)) {
    return Object.values(RoutingKeys).find((routingKey) =>
      activeRoutingKey.startsWith(routingKey),
    );
  }
  return undefined;
}

export function getSeriesRoutingKey(...args: string[]): string {
  return constructRoutingKey(RoutingKeys.SERIES, ...args);
}

export function getModelsRoutingKey(...args: string[]): string {
  return constructRoutingKey(RoutingKeys.MODELS, ...args);
}

export function getDealerDetailsRoutingKey(...args: string[]): string {
  return constructRoutingKey(RoutingKeys.DEALER_SEARCH, ...args);
}

export function getAccountRoutingKey(...args: string[]): string {
  return constructRoutingKey(RoutingKeys.ACCOUNT, ...args);
}

export function getVehiclePurchaseRoutingKey(...args: string[]): string {
  return constructRoutingKey(RoutingKeys.VEHICLE_PURCHASE, ...args);
}

export function getLastRoutingSegment(routingKey: string): string {
  return routingKey.split("/").slice(-1)[0];
}

export function removeLastRoutingSegment(routingKey: string): string {
  return routingKey.split("/").slice(0, -1).join("/");
}

export function determineMainDrawerLevel(activeRoutingKey: string): number {
  const topLevelRoutingKey = findTopLevelRoutingKey(activeRoutingKey);
  switch (topLevelRoutingKey) {
    case RoutingKeys.MAIN_MENU: {
      return 1;
    }
    case activeRoutingKey: {
      return 2;
    }
    default: {
      return activeRoutingKey.split("/").length + 1;
    }
  }
}

export function getPreviousRoutingKey(routingKey: string): string {
  const previousRoutingKey = removeLastRoutingSegment(routingKey);
  if (!isString(previousRoutingKey)) {
    return RoutingKeys.MAIN_MENU;
  }

  return previousRoutingKey;
}
